import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

export const getWarehousesList = createAsyncThunk(
    'warehouse/getWarehousesList',
    async ({ accountId, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/products/warehousesList?accountId=${accountId}`;
        
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            }
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante il recupero dei magazzini');
        }
        
        const data = await response.json();
        return data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const getWarehouseDetail = createAsyncThunk(
    'warehouse/getWarehouseDetail',
    async ({ warehouseId, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/products/warehouseDetail?warehouseId=${warehouseId}`;
        
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            }
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante il recupero dei dettagli del magazzino');
        }
        
        const data = await response.json();
        return data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const createWarehouse = createAsyncThunk(
    'warehouse/createWarehouse',
    async ({ warehouse, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/products/warehouseCreate`;
        
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            },
            body: JSON.stringify({ warehouse })
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante la creazione del magazzino');
        }
        
        const data = await response.json();
        return data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const updateWarehouse = createAsyncThunk(
    'warehouse/updateWarehouse',
    async ({ warehouse, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/products/warehouseSave`;
        
        const response = await fetch(url, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            },
            body: JSON.stringify({ warehouse })
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante l\'aggiornamento del magazzino');
        }
        
        const data = await response.json();
        return data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const deleteWarehouse = createAsyncThunk(
    'warehouse/deleteWarehouse',
    async ({ warehouseId, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/products/warehouseDelete`;

        const response = await fetch(url, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            },
            body: JSON.stringify({ warehouseId })
        });

        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante l\'eliminazione del magazzino');
        }

        return warehouseId;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const getProductsInWarehouse = createAsyncThunk(
    'warehouse/getProductsInWarehouse',
    async ({ accountId, warehouseId, start, length, search, token }, { rejectWithValue }) => {
    try {
        let url = `${import.meta.env.VITE_API_BASE_URL}/products/list?accountId=${accountId}&start=${start}&length=${length}&draw=1&warehouseId=${warehouseId}`;
        
        // Aggiungo i parametri di ricerca se presenti
        if (search && search.length > 0) {
            url += `&search[value]=${encodeURIComponent(search)}`;
        } else {
            url += `&search[value]=`;
        }
        
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            }
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante il recupero dei prodotti nel magazzino');
        }
        
        const data = await response.json();
        return data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

const warehouseSlice = createSlice({
    name: 'warehouse',
    initialState: {
        warehousesList: [],
        loadingList: false,
        errorList: null,
        selectedWarehouse: null,
        loadingDetail: false,
        errorDetail: null,
        createLoading: false,
        createError: null,
        updateLoading: false,
        updateError: null,
        deleteLoading: false,
        deleteError: null,
        productsInWarehouse: null,
        loadingProducts: false,
        errorProducts: null
    },
    reducers: {
        resetSelectedWarehouse: (state) => {
            state.selectedWarehouse = null;
            state.loadingDetail = false;
            state.errorDetail = null;
        },
        resetCreateStatus: (state) => {
            state.createLoading = false;
            state.createError = null;
        },
        resetUpdateStatus: (state) => {
            state.updateLoading = false;
            state.updateError = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // getWarehousesList
            .addCase(getWarehousesList.pending, (state) => {
                state.loadingList = true;
                state.errorList = null;
            })
            .addCase(getWarehousesList.fulfilled, (state, action) => {
                state.loadingList = false;
                state.warehousesList = action.payload;
            })
            .addCase(getWarehousesList.rejected, (state, action) => {
                state.loadingList = false;
                state.errorList = action.payload || 'Errore durante il recupero dei magazzini';
            })
            // getWarehouseDetail
            .addCase(getWarehouseDetail.pending, (state) => {
                state.loadingDetail = true;
                state.errorDetail = null;
            })
            .addCase(getWarehouseDetail.fulfilled, (state, action) => {
                state.loadingDetail = false;
                state.selectedWarehouse = action.payload;
            })
            .addCase(getWarehouseDetail.rejected, (state, action) => {
                state.loadingDetail = false;
                state.errorDetail = action.payload || 'Errore durante il recupero dei dettagli del magazzino';
            })
            // createWarehouse
            .addCase(createWarehouse.pending, (state) => {
                state.createLoading = true;
                state.createError = null;
            })
            .addCase(createWarehouse.fulfilled, (state, action) => {
                state.createLoading = false;
                state.warehousesList = [...state.warehousesList, action.payload];
            })
            .addCase(createWarehouse.rejected, (state, action) => {
                state.createLoading = false;
                state.createError = action.payload || 'Errore durante la creazione del magazzino';
            })
            // updateWarehouse
            .addCase(updateWarehouse.pending, (state) => {
                state.updateLoading = true;
                state.updateError = null;
            })
            .addCase(updateWarehouse.fulfilled, (state, action) => {
                state.updateLoading = false;
                state.selectedWarehouse = action.payload;
                // Aggiorna anche l'elenco dei magazzini
                state.warehousesList = state.warehousesList.map(warehouse => 
                    warehouse._id === action.payload._id ? action.payload : warehouse
                );
            })
            .addCase(updateWarehouse.rejected, (state, action) => {
                state.updateLoading = false;
                state.updateError = action.payload || 'Errore durante l\'aggiornamento del magazzino';
            })
            // deleteWarehouse
            .addCase(deleteWarehouse.pending, (state) => {
                state.deleteLoading = true;
                state.deleteError = null;
            })
            .addCase(deleteWarehouse.fulfilled, (state, action) => {
                state.deleteLoading = false;
                state.warehousesList = state.warehousesList.filter(w => w._id !== action.payload);
                state.selectedWarehouse = null;
            })
            .addCase(deleteWarehouse.rejected, (state, action) => {
                state.deleteLoading = false;
                state.deleteError = action.payload || 'Errore durante l\'eliminazione del magazzino';
            })
            // getProductsInWarehouse
            .addCase(getProductsInWarehouse.pending, (state) => {
                state.loadingProducts = true;
                state.errorProducts = null;
            })
            .addCase(getProductsInWarehouse.fulfilled, (state, action) => {
                state.loadingProducts = false;
                state.productsInWarehouse = action.payload;
            })
            .addCase(getProductsInWarehouse.rejected, (state, action) => {
                state.loadingProducts = false;
                state.errorProducts = action.payload || 'Errore durante il recupero dei prodotti nel magazzino';
            });
    }
});

export const { resetSelectedWarehouse, resetCreateStatus, resetUpdateStatus } = warehouseSlice.actions;

export const selectWarehousesList = (state) => state.warehouse.warehousesList;
export const selectWarehousesLoading = (state) => state.warehouse.loadingList;
export const selectWarehousesError = (state) => state.warehouse.errorList;
export const selectSelectedWarehouse = (state) => state.warehouse.selectedWarehouse;
export const selectWarehouseDetailLoading = (state) => state.warehouse.loadingDetail;
export const selectWarehouseDetailError = (state) => state.warehouse.errorDetail;
export const selectCreateWarehouseLoading = (state) => state.warehouse.createLoading;
export const selectCreateWarehouseError = (state) => state.warehouse.createError;
export const selectUpdateWarehouseLoading = (state) => state.warehouse.updateLoading;
export const selectUpdateWarehouseError = (state) => state.warehouse.updateError;
export const selectProductsInWarehouse = (state) => state.warehouse.productsInWarehouse;
export const selectProductsInWarehouseLoading = (state) => state.warehouse.loadingProducts;
export const selectProductsInWarehouseError = (state) => state.warehouse.errorProducts;

export default warehouseSlice.reducer;
